import {
  createContext,
  useContext,
  memo,
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  useThread,
  useThreadRuntime,
} from '@assistant-ui/react';
import ReactMarkdown from 'react-markdown';
import ReactECharts from 'echarts-for-react';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { useCopilotMin } from './CopilotMinContext';
import { buildCopilotHeaders } from './copilotSession';
import { ReactComponent as AiIcon } from '../../assets/ai-stars.svg';
import { ReactComponent as CopyIcon } from '../CopilotButtonAssistantUI/assets/copy.svg';
import { ReactComponent as ThumbUpIcon } from '../CopilotButtonAssistantUI/assets/like.svg';
import { ReactComponent as ThumbUpFilledIcon } from '../CopilotButtonAssistantUI/assets/like-filled.svg';
import { ReactComponent as ThumbDownIcon } from '../CopilotButtonAssistantUI/assets/dislike.svg';
import { ReactComponent as ThumbDownFilledIcon } from '../CopilotButtonAssistantUI/assets/dislike-filled.svg';
import { ReactComponent as NewChatIcon } from '../CopilotButtonAssistantUI/assets/new-chat.svg';
import { ReactComponent as CloseIcon } from '../CopilotButtonAssistantUI/assets/close.svg';
import { ReactComponent as SubmitIcon } from '../CopilotButtonAssistantUI/assets/submit.svg';
import { ReactComponent as StopIcon } from '../CopilotButtonAssistantUI/assets/stop.svg';
import { ReactComponent as ReportIcon } from '../CopilotButtonAssistantUI/assets/report.svg';
import { ReactComponent as ExportIcon } from '../CopilotButtonAssistantUI/assets/export.svg';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { API } from '../../API';
import PublicationsPanel from './PublicationsPanel';
import ReportPanel from './ReportPanel';
import EmptyState from './EmptyState';
import {
  CopilotAnalyticsContext,
  useCopilotTrack,
  trackCopilotEvent,
  describeResponse,
  parseReferenceCount,
} from '../CopilotButtonAssistantUI/analytics';
import {
  FILE_LINK_RE,
  parseFileRef,
  isSameFileRef,
  isPdfName,
  findLastPdfRef,
  normalizeFileLinks,
  maskPartialFileLink,
  fetchCopilotFile,
  fileErrorMessage,
  saveBlob,
} from './copilotFileApi';
import { osavulContextOf } from './copilotScreen';
import { storedMessageOf } from './copilotHistory';
import { activityLabel, addNarration, applyStepEvent, finishRunning, groupSteps, statusLabel, stepCount, thoughtText, STEP_ERROR } from './copilotSteps';
import { approvalOf, colorsOf, interruptResponseOf } from './copilotApproval';
import styles from './CopilotMinButton.module.scss';

// ---------------------------------------------------------------------------
// Context — passes the "open publications panel" callback into markdown renderers
// ---------------------------------------------------------------------------
const CopilotUIContext = createContext(null);

// ---------------------------------------------------------------------------
// ECharts block — renders ```echarts ... ``` code blocks as interactive charts
// ---------------------------------------------------------------------------
const EChartsBlock = ({ children }) => {
  const { t } = useTranslation();
  const track = useCopilotTrack();
  const chartRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  let option = null;
  let parseError = false;
  try {
    option = JSON.parse(String(children).trim());
  } catch {
    parseError = true;
  }

  const chartTitle = Array.isArray(option?.title)
    ? option?.title[0]?.text
    : option?.title?.text;

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  if (parseError) {
    return (
      <pre className={styles.codeBlock}>
        <code>{children}</code>
      </pre>
    );
  }

  const triggerDownload = (url, filename) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleExportJpeg = () => {
    const inst = chartRef.current?.getEchartsInstance();
    if (!inst) return;
    track('Export copilot chart', { format: 'jpeg' });
    triggerDownload(
      inst.getDataURL({ type: 'jpeg', pixelRatio: 2, backgroundColor: '#fff' }),
      'chart.jpg',
    );
    setDropdownOpen(false);
  };

  const handleExportJson = () => {
    track('Export copilot chart', { format: 'json' });
    const blob = new Blob([JSON.stringify(option, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    triggerDownload(url, 'chart.json');
    URL.revokeObjectURL(url);
    setDropdownOpen(false);
  };

  const handleExportPdf = () => {
    const inst = chartRef.current?.getEchartsInstance();
    if (!inst) return;
    track('Export copilot chart', { format: 'pdf' });
    const dataUrl = inst.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
    import('jspdf').then(({ jsPDF }) => {
      const pdf = new jsPDF({ orientation: 'landscape' });
      const w = pdf.internal.pageSize.getWidth();
      const h = pdf.internal.pageSize.getHeight();
      pdf.addImage(dataUrl, 'PNG', 0, 0, w, h);
      pdf.save(`${chartTitle || 'chart'}.pdf`);
      setDropdownOpen(false);
    }).catch((err) => {
      console.error('PDF export failed:', err);
    });
  };

  return (
    <div className={styles.chartBlock}>
      <div className={styles.chartHeader}>
        {chartTitle && <span className={styles.chartTitle}>{chartTitle}</span>}
        <div className={styles.exportWrapper} ref={dropdownRef}>
          <button
            type="button"
            className={styles.exportIconBtn}
            onClick={() => setDropdownOpen((o) => !o)}
            aria-label={t('Export chart')}
            aria-expanded={dropdownOpen}
            aria-haspopup="menu"
            data-tooltip-id='export-chart-data'
            data-tooltip-content={t('Download')}
          >
            <ExportIcon aria-hidden="true" />
          </button>
          {dropdownOpen && (
            <div className={styles.exportDropdown} role="menu">
              <button type="button" role="menuitem" className={styles.exportDropdownItem} onClick={handleExportPdf}>
                {t('Download as PDF')}
              </button>
              <button type="button" role="menuitem" className={styles.exportDropdownItem} onClick={handleExportJson}>
                {t('Download as JSON')}
              </button>
              <button type="button" role="menuitem" className={styles.exportDropdownItem} onClick={handleExportJpeg}>
                {t('Download as JPEG')}
              </button>
            </div>
          )}
        </div>
      </div>
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ height: 280, width: '100%' }}
        opts={{ renderer: 'canvas' }}
        notMerge
      />
      <Tooltip id='export-chart-data' place='bottom' style={{ zIndex: 310, fontSize: '12px', padding: '4px 8px', lineHeight: '1.25rem' }} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Markdown renderers
// ---------------------------------------------------------------------------

const PreRenderer = ({ children }) => {
  const codeEl = Array.isArray(children) ? children[0] : children;
  const className = codeEl?.props?.className || '';
  const lang = /language-(\w+)/.exec(className)?.[1];

  if (lang === 'echarts') {
    return <EChartsBlock>{codeEl?.props?.children}</EChartsBlock>;
  }

  return <pre className={styles.codeBlock}>{children}</pre>;
};

const CodeRenderer = ({ className, children }) => {
  // The agent sometimes puts a file link inside a code span or fence, where
  // markdown never parses links — detect it and render the card anyway.
  const text = typeof children === 'string' ? children.trim() : '';
  if (/^\S+$/.test(text) && FILE_LINK_RE.test(text)) {
    return <FileCard url={text} name="" />;
  }
  return <code className={className}>{children}</code>;
};

const LinkRenderer = ({ href, children }) => {
  const ctx = useContext(CopilotUIContext);
  if (href === '#file-loading') {
    return <FileCard url={null} name="" />;
  }
  if (href?.startsWith('#ref-')) {
    return (
      <button
        type="button"
        className={styles.referenceBtn}
        onClick={() => ctx?.onReferenceClick?.(href, String(children))}
      >
        {children}
      </button>
    );
  }
  if (href && FILE_LINK_RE.test(href)) {
    return <FileCard url={href} name={nodeText(children)} />;
  }
  const isExternal = href?.startsWith('http://') || href?.startsWith('https://');
  return (
    <a href={href} {...(isExternal && { target: '_blank', rel: 'noreferrer' })}>
      {children}
    </a>
  );
};

// The text of a link's children (a string, or an array of strings and elements).
const nodeText = (node) =>
  typeof node === 'string' ? node : Array.isArray(node) ? node.map(nodeText).join('') : '';

const MARKDOWN_COMPONENTS = { pre: PreRenderer, code: CodeRenderer, a: LinkRenderer };

// ---------------------------------------------------------------------------
// Message actions — copy, like, dislike
// ---------------------------------------------------------------------------
const MessageActions = ({ text, t, showCopy = true, traceId, onFeedback }) => {
  const track = useCopilotTrack();
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);

  const handleCopy = () => {
    track('Copy copilot response', { response_length: text.length });
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Rating is tracked in Amplitude on every "turn on" click regardless of
  // traceId — the backend feedback call below is skipped without one, so this
  // is the only record of the rating in that case.
  const handleLike = () => {
    const next = !liked;
    setLiked(next);
    if (disliked) setDisliked(false);
    if (next) track('Rate copilot response', { rating: 'positive', has_trace_id: Boolean(traceId) });
    if (next && traceId) onFeedback?.(traceId, 'like');
  };

  const handleDislike = () => {
    const next = !disliked;
    setDisliked(next);
    if (liked) setLiked(false);
    if (next) track('Rate copilot response', { rating: 'negative', has_trace_id: Boolean(traceId) });
    if (next && traceId) onFeedback?.(traceId, 'dislike');
  };

  return (
    <div className={styles.messageActions}>
      {showCopy && (
        <button
          type="button"
          className={`${styles.messageActionBtn} ${copied ? styles.messageActionBtnActive : ''}`}
          onClick={handleCopy}
          aria-label={copied ? t('Copied!') : t('Copy to clipboard')}
          data-tooltip-id="msg-actions-tooltip"
          data-tooltip-content={copied ? t('Copied!') : t('Copy to clipboard')}
        >
          <CopyIcon aria-hidden="true" />
        </button>
      )}
      <button
        type="button"
        className={`${styles.messageActionBtn} ${liked ? styles.messageActionBtnActive : ''}`}
        onClick={handleLike}
        aria-label={t('Like')}
        aria-pressed={liked}
        data-tooltip-id="msg-actions-tooltip"
        data-tooltip-content={t('Like')}
      >
        {liked ? <ThumbUpFilledIcon aria-hidden="true" /> : <ThumbUpIcon aria-hidden="true" />}
      </button>
      <button
        type="button"
        className={`${styles.messageActionBtn} ${disliked ? styles.messageActionBtnActive : ''}`}
        onClick={handleDislike}
        aria-label={t('Dislike')}
        aria-pressed={disliked}
        data-tooltip-id="msg-actions-tooltip"
        data-tooltip-content={t('Dislike')}
      >
        {disliked ? <ThumbDownFilledIcon aria-hidden="true" /> : <ThumbDownIcon aria-hidden="true" />}
      </button>
      <Tooltip id="msg-actions-tooltip" place="bottom" style={{ zIndex: 310, fontSize: '12px', padding: '4px 8px', lineHeight: '1.25rem' }} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// File card — rendered in place of a Copilot file link inside a message.
// Uses <span> elements (not <div>) because markdown links render inside <p>.
// A PDF previews in the side panel and downloads; any other file downloads only.
// ---------------------------------------------------------------------------
const FILE_KINDS = [
  { re: /\.pdf$/i, subtitle: 'PDF document' },
  { re: /\.xlsx?$/i, subtitle: 'Excel workbook' },
  { re: /\.docx?$/i, subtitle: 'Word document' },
  { re: /\.csv$/i, subtitle: 'CSV file' },
  { re: /\.(md|txt)$/i, subtitle: 'Text document' },
  { re: /\.(png|jpe?g|svg)$/i, subtitle: 'Image' },
];

const FileCard = ({ url, name: linkName }) => {
  const { t } = useTranslation();
  const track = useCopilotTrack();
  const ctx = useContext(CopilotUIContext);
  const [downloading, setDownloading] = useState(false);
  // A link's label is the file name only when the agent wrote one: a bare, backticked or loading link
  // has none (or the URL itself), so the real name comes from the file response on first use.
  const [resolvedName, setResolvedName] = useState('');
  const writtenName = linkName && !/^(https?:)?\/\//.test(linkName) && !FILE_LINK_RE.test(linkName) ? linkName : '';
  const name = writtenName || resolvedName;
  // The card keeps working long after it was written: the link holds only the
  // file id, which the API resolves (and authorizes) on every request.
  const fileRef = useMemo(() => {
    const parsed = parseFileRef(url);
    return parsed ? { ...parsed, name } : null;
  }, [url, name]);
  // A report link of the previous Copilot is always a PDF, even before its name is known.
  const canPreview = Boolean(fileRef) && (isPdfName(name) || fileRef.narrativeId != null);
  // The card doubles as the toggle for the preview panel showing its file.
  const isPreviewOpen = canPreview && isSameFileRef(ctx?.activeReport, fileRef) && !ctx?.reportClosing;
  const subtitle = FILE_KINDS.find((kind) => kind.re.test(name))?.subtitle ?? 'File';

  const handlePreview = () => {
    if (!canPreview) return;
    if (!isPreviewOpen) track('Preview copilot report', { source: 'card' });
    ctx?.onReportToggle?.(fileRef);
  };

  const handleDownload = async (openPdf = false) => {
    if (!fileRef) return;
    setDownloading(true);
    try {
      const { blob, fileName } = await fetchCopilotFile(fileRef);
      if (!name && fileName) setResolvedName(fileName);
      // An unnamed card learned here that it holds a PDF: preview it instead of saving.
      if (openPdf && isPdfName(fileName)) {
        track('Preview copilot report', { source: 'card' });
        ctx?.onReportToggle?.({ ...fileRef, name: fileName });
        return;
      }
      track('Download copilot report');
      saveBlob(blob, fileName);
    } catch (error) {
      toast.error(fileErrorMessage(error, t));
    } finally {
      setDownloading(false);
    }
  };

  // The card body is the preview control (the download for a non-PDF) and
  // Download its own button, so both are real buttons (no nested interactive
  // elements) and keyboard users get two tab stops with clear names.
  return (
    <span className={styles.reportCard}>
      <button
        type="button"
        className={styles.reportCardMain}
        onClick={canPreview ? handlePreview : () => handleDownload(!name)}
        disabled={!fileRef || downloading}
        aria-label={canPreview ? t('Preview report') : t('Download file')}
        aria-expanded={canPreview ? isPreviewOpen : undefined}
      >
        <span className={styles.reportCardIconTile} aria-hidden="true">
          <ReportIcon className={styles.reportCardIcon} />
        </span>
        <span className={styles.reportCardText}>
          <span className={styles.reportCardTitle}>{name || t('File')}</span>
          <span className={styles.reportCardSubtitle}>{t(subtitle)}</span>
        </span>
      </button>
      <button
        type="button"
        className={styles.reportCardDownloadBtn}
        onClick={() => handleDownload()}
        disabled={!fileRef || downloading}
        aria-busy={downloading}
        aria-label={t('Download file')}
      >
        {t('Download')}
      </button>
    </span>
  );
};

// ---------------------------------------------------------------------------
// Steps — the tool calls behind an answer, collapsed under its text
// ---------------------------------------------------------------------------
const STREAMING_ROWS = 3;
const failedNote = (row, t) => (row.count > 1 ? `${row.failed} ${t('of')} ${row.count} ${t('failed')}` : t('failed'));

const Dots = () => (
  <span className={styles.dots} aria-hidden="true">
    <span />
    <span />
    <span />
  </span>
);

const StepRow = ({ row, t }) =>
  row.kind === 'thought' ? (
    <div role="listitem" className={styles.thoughtRow}>
      <span className={styles.stepMark} aria-hidden="true" data-thought="true" />
      <span className={styles.thoughtText}>{thoughtText(row.text)}</span>
    </div>
  ) : (
  <div role="listitem" className={styles.stepRow} data-status={row.status}>
    <span className={styles.stepMark} aria-hidden="true" />
    <span className={styles.stepLabel}>{row.label}</span>
    {row.status === 'running' && <Dots />}
    {row.count > 1 && (
      <span className={styles.stepCount} title={`${row.count} ${t('times')}`}>
        {row.count}
      </span>
    )}
    {row.status === STEP_ERROR && <span className={styles.stepFailed}>{failedNote(row, t)}</span>}
  </div>
  );


const StepsList = ({ steps, t, isStreaming, waiting, activity }) => {
  const [folded, setFolded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  if (!steps?.length) return null;
  const rows = groupSteps(steps);
  const summary = `${t('Steps')} (${stepCount(steps)})`;
  if (isStreaming) {
    const live = waiting && !steps.some((s) => s.status === 'running') ? { id: 'live', label: activity || t('Generating'), status: 'running', count: 1, failed: 0 } : null;
    const current = live ?? [...rows].reverse().find((row) => row.status === 'running');
    const shown = showAll ? rows : rows.slice(-STREAMING_ROWS);
    const earlier = rows.slice(0, rows.length - shown.length).reduce((sum, row) => sum + row.count, 0);
    return (
      <div className={styles.steps}>
        <button type="button" className={styles.stepsToggle} aria-expanded={!folded} onClick={() => setFolded(!folded)}>
          {summary}
        </button>
        {!folded && (
          <div role="list" className={styles.stepsList}>
            {earlier > 0 && (
              <button type="button" className={styles.stepsEarlier} onClick={() => setShowAll(true)}>
                {`+${earlier} ${t('earlier')}`}
              </button>
            )}
            {shown.map((row) => (
              <StepRow key={row.id} row={row} t={t} />
            ))}
            {live && <StepRow row={live} t={t} />}
          </div>
        )}
        {folded && current && (
          <div role="list" className={styles.stepsList}>
            <StepRow row={current} t={t} />
          </div>
        )}
      </div>
    );
  }
  return (
    <details className={styles.steps}>
      <summary className={styles.stepsSummary}>{summary}</summary>
      <div role="list" className={styles.stepsList}>
        {rows.map((row) => (
          <StepRow key={row.id} row={row} t={t} />
        ))}
      </div>
    </details>
  );
};

// ---------------------------------------------------------------------------
// Approval — Copilot paused on a change and waits for the user's yes or no
// ---------------------------------------------------------------------------
// A long argument shows its start; a click shows all of it.
const APPROVAL_VALUE_PREVIEW_CHARS = 120;

const ApprovalValue = ({ value, t }) => {
  const [expanded, setExpanded] = useState(false);
  if (value.length <= APPROVAL_VALUE_PREVIEW_CHARS) return value;
  return (
    <>
      {expanded ? value : `${value.slice(0, APPROVAL_VALUE_PREVIEW_CHARS)}...`}{' '}
      <button type="button" className={styles.approvalMore} aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
        {expanded ? t('Show less') : t('Show more')}
      </button>
    </>
  );
};

const ApprovalRows = ({ rows, t }) => (
  <dl className={styles.approvalDetails}>
    {rows.map(([name, value]) => {
      const colors = name === 'colors' ? colorsOf(value) : null;
      return (
        <div key={name} className={styles.approvalDetail}>
          <dt>{name}</dt>
          <dd>
            {colors ? (
              <span className={styles.swatches}>
                {colors.map((color) => (
                  <span key={color} className={styles.swatch} style={{ background: color }} title={color} />
                ))}
                {value}
              </span>
            ) : (
              <ApprovalValue value={value} t={t} />
            )}
          </dd>
        </div>
      );
    })}
  </dl>
);

// A card is dropped when the user wrote on without answering it: the next user message is not the "Approved" / "Refused" its buttons send.
// Read from the messages themselves, so it holds after a reload or when the panel is reopened.
const isApprovalDropped = (messages, i, t) => {
  if (!messages[i]?.metadata?.custom?.approval) return false;
  const next = messages.slice(i + 1).find((m) => m.role === 'user');
  if (!next) return false;
  const text = next.content?.find?.((p) => p.type === 'text')?.text ?? next.content;
  return text !== t('Approved') && text !== t('Refused');
};

const ApprovalCard = ({ approval, open, dropped, t }) => {
  const ctx = useContext(CopilotUIContext);
  return (
    <div className={styles.approval} role="group" aria-label={t('Copilot needs your approval')}>
      <p className={styles.approvalTitle}>{t('Copilot needs your approval')}</p>
      {approval.actions.map((action) => (
        <div key={action.id} className={styles.approvalAction}>
          <p className={styles.approvalWhat}>{action.title}</p>
          {action.details.length > 0 && <ApprovalRows rows={action.details} t={t} />}
          {action.advanced.length > 0 && (
            <details className={styles.approvalAdvanced}>
              <summary>{t('Advanced')}</summary>
              <ApprovalRows rows={action.advanced} t={t} />
            </details>
          )}
        </div>
      ))}
      {open && (
        <div className={styles.approvalButtons}>
          <button type="button" className={styles.approvalApprove} onClick={() => ctx?.onApproval?.(approval.id, true)}>
            {t('Approve')}
          </button>
          <button type="button" className={styles.approvalRefuse} onClick={() => ctx?.onApproval?.(approval.id, false)}>
            {t('Refuse')}
          </button>
        </div>
      )}
      {dropped && <p className={styles.approvalDropped}>{t('Not answered: the change did not run')}</p>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Message rendering
// ---------------------------------------------------------------------------

const MessageItem = ({ message, t, isStreaming, traceId, onFeedback, approvalOpen, approvalDropped }) => {
  const text = message.content.find((p) => p.type === 'text')?.text ?? '';
  const status = message.status;
  const isError = status?.type === 'incomplete' && status?.reason === 'error';

  if (message.role === 'user') {
    return (
      <div className={styles.userMessage} role="article" aria-label="Your message">
        <p>{text}</p>
      </div>
    );
  }

  if (message.role === 'assistant') {
    if (isError) {
      return (
        <div className={styles.assistantMessage} role="article" aria-label="Copilot error">
          <p className={styles.errorInlineMessage} role="alert">
            {t("Couldn't get a response. Please try again.")}
          </p>
        </div>
      );
    }

    const displayText = normalizeFileLinks(isStreaming ? maskPartialFileLink(text) : text);
    const steps = message.metadata?.custom?.steps;
    const activity = activityLabel(message.metadata?.custom);
    const approval = message.metadata?.custom?.approval;

    return (
      <div className={`${styles.assistantMessage} ${isStreaming ? styles.assistantStreaming : ''}`} role="article" aria-label="Copilot response">
        <StepsList steps={steps} t={t} isStreaming={isStreaming} waiting={isStreaming && !text.trim()} activity={activity} />
        <ReactMarkdown components={MARKDOWN_COMPONENTS}>{displayText}</ReactMarkdown>
        {approval && <ApprovalCard approval={approval} open={approvalOpen && !isStreaming && (!approval.outcome || approval.outcome === 'pending')} dropped={approvalDropped || approval.outcome === 'moved_on'} t={t} />}
        {!isStreaming && text.trim() && <MessageActions text={text} t={t} traceId={traceId} onFeedback={onFeedback} />}
      </div>
    );
  }

  return null;
};

// What Copilot is doing right now: its running tool (`stepLabel`), or a generic
// line while the model thinks between tools.
const TypingIndicator = ({ t, stepLabel }) => {
  const label = stepLabel || t('Thinking');

  return (
    <div className={styles.loadingState} role="status" aria-live="polite" aria-label={label}>
      <span key={label} className={styles.loadingText}>
        {label}
        <Dots />
      </span>
    </div>
  );
};

// A request that was in flight when the tab reloaded. The backend cancels the
// turn on disconnect, so the request is shown outside the history followed by
// the same inline error Copilot uses for a failed answer, plus a retry (the
// request re-enters the history only when resent).
const InterruptedRequest = ({ t, interrupted, onSendAgain }) => (
  <>
    <div className={styles.userMessage} role="article" aria-label={t('Your message')}>
      <p>{interrupted.text}</p>
    </div>
    <div className={styles.assistantMessage} role="article" aria-label={t('Copilot error')}>
      <p className={styles.errorInlineMessage} role="alert">
        {t('Response generation was interrupted.')}{' '}
        <button type="button" className={styles.retryButton} onClick={onSendAgain}>
          {t('Retry')}
        </button>
      </p>
    </div>
  </>
);

// Reads messages from the thread context; shows EmptyState when empty
const MessageList = ({ t, onSelectTemplate, context, onShowPublications, traceIdMap, onFeedback, interrupted, onSendAgain }) => {
  const messages = useThread((s) => s.messages);
  const isRunning = useThread((s) => s.isRunning);
  const endRef = useRef(null);
  // The newest step moves the view too, not only a new message, so the list and the loader stay together.
  const stepCount = messages[messages.length - 1]?.metadata?.custom?.steps?.length ?? 0;
  // The approval card arrives inside the last message when the run ends, so it needs its own trigger or it stays below the fold.
  const approval = messages[messages.length - 1]?.metadata?.custom?.approval;
  const approvalWaiting = !!approval && (!approval.outcome || approval.outcome === 'pending');

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, stepCount, approvalWaiting, isRunning]);

  if (messages.length === 0 && !interrupted) {
    return <EmptyState t={t} onSelectTemplate={onSelectTemplate} context={context} onShowPublications={onShowPublications} />;
  }

  return (
    <div className={styles.messageList} role="log" aria-live="polite" aria-label={t('Chat history')}>
      {messages.map((msg, i) => (
        <MessageItem
          key={msg.id ?? i}
          message={msg}
          t={t}
          isStreaming={isRunning && i === messages.length - 1}
          approvalOpen={!isRunning && i === messages.length - 1}
          approvalDropped={isApprovalDropped(messages, i, t)}
          traceId={traceIdMap?.get(msg.id ?? i)}
          onFeedback={onFeedback}
        />
      ))}
      {interrupted && <InterruptedRequest t={t} interrupted={interrupted} onSendAgain={onSendAgain} />}
      <div ref={endRef} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Composer (input area)
// ---------------------------------------------------------------------------
const MAX_TEXTAREA_HEIGHT = 120; // ~5 lines before scroll

const ChatComposer = ({ t, onSubmit, isRunning, onStop, context }) => {
  const [text, setText] = useState('');
  const textareaRef = useRef(null);

  const placeholder =
    context === 'dashboard'
      ? t('Ask about this dashboard...')
      : context === 'case'
        ? t('Ask about this case...')
        : t('Ask about your data...');
  const inputDisabled = isRunning;

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
  }, []);

  const handleChange = (e) => {
    setText(e.target.value);
    adjustHeight();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || inputDisabled) return;
    onSubmit(trimmed);
    setText('');
    // Reset height after send
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.overflowY = 'hidden';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form className={styles.composer} onSubmit={handleSubmit} aria-label={t('Send a message')}>
      <label htmlFor="copilot-aui-input" className={styles.srOnly}>
        {placeholder}
      </label>
      <div className={styles.composerBox}>
        <textarea
          ref={textareaRef}
          id="copilot-aui-input"
          className={styles.composerInput}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          disabled={inputDisabled}
          aria-disabled={inputDisabled}
        />
        {isRunning ? (
          <button
            type="button"
            className={styles.stopBtn}
            onClick={onStop}
            aria-label={t('Stop generation')}
          >
            <StopIcon aria-hidden="true" />
          </button>
        ) : (
          <button
            type="submit"
            className={`${styles.sendBtn} ${!text.trim() ? styles.sendBtnDisabled : ''}`}
            disabled={!text.trim()}
            aria-label={t('Send message')}
          >
            <SubmitIcon aria-hidden="true" />
          </button>
        )}
      </div>
      <p className={styles.disclaimer}>{t('AI can make mistakes')}</p>
    </form>
  );
};

// The case a `#ref-publications?...&case=<id>` link was made for. Older links have none.
const referenceCase = (href) => {
  const value = new URLSearchParams(href?.split('?')[1] ?? '').get('case');
  return value && /^\d+$/.test(value) ? Number(value) : null;
};

// ---------------------------------------------------------------------------
// Inner shell — must live inside AssistantRuntimeProvider to use thread hooks
// ---------------------------------------------------------------------------
const CopilotInner = ({
  t,
  onClose,
  onNewChat,
  onStop,
  canStartNewChat,
  context,
  conversation,
  traceIdMap,
  autoReport,
  clearAutoReport,
  reportModeRef,
  interruptRef,
  lastTurnModeRef,
  runStartedAtRef,
}) => {
  const threadRuntime = useThreadRuntime();
  const track = useCopilotTrack();
  const isRunning = useThread((s) => s.isRunning);
  // The tool Copilot is running for the answer being written, if any.
  // The loader is only for the wait before anything shows: the first step or answer text replaces it.
  const hideLoader = useThread((s) => {
    const last = s.messages[s.messages.length - 1];
    if (last?.role !== 'assistant') return false;
    return last.content?.some?.((p) => p.type === 'text' && p.text?.trim()) || Boolean(last.metadata?.custom?.steps?.length);
  });
  const activeStepLabel = useThread((s) => {
    const last = s.messages[s.messages.length - 1];
    return last?.role === 'assistant' ? activityLabel(last.metadata?.custom) : null;
  });
  const { page, screen } = useCopilotMin();
  // Side panel left of the chat: publications or a report preview, never both.
  const [activeRef, setActiveRef] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  // True while the report panel plays its slide-out; it unmounts via onClose.
  const [reportClosing, setReportClosing] = useState(false);
  const { interrupted } = conversation;

  const sendFeedback = useCallback(async (traceId, sentiment) => {
    const res = await API.Fetch('POST', '/API/v2/copilot/feedback', null, { trace_id: traceId, sentiment });
    if (!res || !res.ok) {
      toast.error(t("Couldn't get a response. Please try again."));
    }
  }, [t]);

  const handleFeedback = useCallback(
    (traceId, sentiment) => sendFeedback(traceId, sentiment),
    [sendFeedback],
  );

  const handleReferenceClick = useCallback((href, label, source = 'message_link') => {
    track('Open copilot references', { count: parseReferenceCount(href), source });
    setActiveReport(null);
    setReportClosing(false);
    setActiveRef({ href, label });
  }, [track]);

  const handleReportPreview = useCallback((reportRef) => {
    setActiveRef(null);
    setActiveReport(reportRef);
    setReportClosing(false);
  }, []);

  const handleReportClosed = useCallback(() => {
    setActiveReport(null);
    setReportClosing(false);
  }, []);

  // Card click: open its report, switch to it, or — when it is the one already
  // showing — close the panel. Reopening mid-animation just cancels the close.
  const handleReportToggle = useCallback(
    (reportRef) => {
      if (isSameFileRef(activeReport, reportRef) && !reportClosing) {
        setReportClosing(true);
      } else {
        handleReportPreview(reportRef);
      }
    },
    [activeReport, reportClosing, handleReportPreview],
  );

  // Open a freshly generated report (or its newest revision) as soon as the
  // panel is showing — immediately if open, otherwise on the next open.
  useEffect(() => {
    if (!autoReport) return;
    track('Preview copilot report', { source: 'auto' });
    handleReportPreview(autoReport);
    clearAutoReport();
  }, [autoReport, handleReportPreview, clearAutoReport, track]);

  const handleShowPublications = useCallback(() => {
    handleReferenceClick('#ref-publications?count=12', '12 Publications', 'suggestion');
  }, [handleReferenceClick]);

  // turn_index = how many user turns the thread already holds (0 = first prompt)
  const userTurnCount = useCallback(
    () => threadRuntime.getState().messages.filter((m) => m.role === 'user').length,
    [threadRuntime],
  );

  // Every send records its mode so the provider can persist what to resend if
  // the tab reloads before the answer arrives. Prompt text never goes to
  // analytics; canned suggestion labels are safe to send.
  const sendMessage = useCallback(
    (text, mode, { source, suggestionLabel } = {}) => {
      track('Submit copilot prompt', {
        source,
        suggestion_label: suggestionLabel,
        mode,
        prompt_length: text.length,
        turn_index: userTurnCount(),
      });
      if (reportModeRef) reportModeRef.current = mode === 'report';
      if (lastTurnModeRef) lastTurnModeRef.current = mode;
      threadRuntime.append({ role: 'user', content: [{ type: 'text', text }] });
    },
    [threadRuntime, reportModeRef, lastTurnModeRef, track, userTurnCount],
  );

  const handleSend = useCallback(
    (text) => {
      // A typed message replaces an answer that never got through: Copilot moves on from the pending change.
      if (interruptRef) interruptRef.current = null;
      sendMessage(text, 'qa', { source: 'typed' });
    },
    [interruptRef, sendMessage],
  );

  // The next turn carries the user's yes or no as interrupt_response.
  const handleApproval = useCallback(
    (id, approve) => {
      if (interruptRef) interruptRef.current = interruptResponseOf(id, approve);
      track('Answer copilot approval', { approve });
      sendMessage(approve ? t('Approved') : t('Refused'), 'qa', { source: 'approval' });
    },
    [interruptRef, sendMessage, track, t],
  );

  const ctxValue = useMemo(
    () => ({
      onReferenceClick: handleReferenceClick,
      onReportToggle: handleReportToggle,
      onApproval: handleApproval,
      activeReport,
      reportClosing,
    }),
    [handleReferenceClick, handleReportToggle, handleApproval, activeReport, reportClosing],
  );

  const handleSelectTemplate = useCallback(
    (query, { report = false } = {}) =>
      sendMessage(query, report ? 'report' : 'qa', {
        source: report ? 'report_template' : 'suggestion',
        suggestionLabel: query,
      }),
    [sendMessage],
  );

  const handleSendAgain = useCallback(() => {
    if (interrupted) sendMessage(interrupted.text, interrupted.mode, { source: 'retry' });
  }, [interrupted, sendMessage]);

  // Goes through the provider so a user-requested stop is told apart from the
  // browser aborting the stream on reload.
  const handleStop = useCallback(() => {
    track('Stop copilot generation', {
      elapsed_ms: runStartedAtRef?.current ? Date.now() - runStartedAtRef.current : null,
    });
    onStop();
  }, [onStop, track, runStartedAtRef]);

  const handleNewChat = useCallback(() => {
    track('Start new copilot chat', { messages_count: threadRuntime.getState().messages.length });
    onNewChat();
  }, [track, threadRuntime, onNewChat]);

  const handleClose = useCallback(() => {
    track('Close copilot', {
      messages_count: threadRuntime.getState().messages.length,
      closed_while_running: isRunning,
    });
    onClose();
  }, [track, threadRuntime, isRunning, onClose]);

  const newChatHint = canStartNewChat ? t('New chat') : t('New chat is unavailable right now.');
  // The conversation follows the user around the app. The bar says which
  // case the next question will be about: the case on screen, or nothing.
  const currentCase = screen?.narrativeId && page?.title ? page : null;

  return (
    <CopilotUIContext.Provider value={ctxValue}>
      <div className={`${styles.chatShell} ${activeRef || activeReport ? styles.chatShellWithPublications : ''}`}>
        {activeRef && (
          <PublicationsPanel
            href={activeRef.href}
            label={activeRef.label}
            narrativeId={referenceCase(activeRef.href) ?? screen?.narrativeId}
            onClose={() => setActiveRef(null)}
          />
        )}
        {activeReport && (
          <ReportPanel
            key={activeReport.fileId}
            reportRef={activeReport}
            closing={reportClosing}
            onClose={handleReportClosed}
          />
        )}

        <div className={styles.modalContent}>
          {/* Header */}
          <div className={styles.modalHeader}>
            <span className={styles.modalTitle}>{t('Osavul Copilot')}</span>
            <div className={styles.headerActions}>
              <button
                type="button"
                className={`${styles.newChatButton} ${!canStartNewChat ? styles.newChatButtonDisabled : ''}`}
                onClick={canStartNewChat ? handleNewChat : undefined}
                aria-disabled={!canStartNewChat}
                aria-label={newChatHint}
                data-tooltip-id="copilot-header-tooltip"
                data-tooltip-content={newChatHint}
              >
                <NewChatIcon aria-hidden="true" />
              </button>
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleClose}
                aria-label={t('Close copilot')}
                data-tooltip-id="copilot-header-tooltip"
                data-tooltip-content={t('Close')}
              >
                <CloseIcon aria-hidden="true" />
              </button>
            </div>
            <Tooltip id="copilot-header-tooltip" place="bottom" style={{ zIndex: 310, fontSize: '12px', padding: '4px 8px' }} />
          </div>

          {currentCase && (
            <Link to={currentCase.path} className={styles.contextBar}>
              <span className={styles.contextBarTitle}>{currentCase.title}</span>
              <span className={styles.contextBarHint}>{t('Copilot answers about this case.')}</span>
            </Link>
          )}

          {/* Chat area */}
          <div className={styles.chatArea}>
            <div className={styles.chatPanel}>
              <div className={styles.messagesScroll}>
                <MessageList
                  t={t}
                  onSelectTemplate={handleSelectTemplate}
                  context={context}
                  onShowPublications={handleShowPublications}
                  traceIdMap={traceIdMap}
                  onFeedback={handleFeedback}
                  interrupted={interrupted}
                  onSendAgain={handleSendAgain}
                />
                {isRunning && !hideLoader && <TypingIndicator t={t} stepLabel={activeStepLabel} />}
              </div>
              <div className={styles.inputArea}>
                <ChatComposer
                  t={t}
                  onSubmit={handleSend}
                  isRunning={isRunning}
                  onStop={handleStop}
                  context={context}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </CopilotUIContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// ConversationBridge — stays mounted for the whole life of a conversation,
// open or closed, so a running answer keeps streaming while the panel is
// hidden or the user is on another page. Mirrors thread changes to the
// provider (persistence, unread) and keeps the per-answer trace ids.
// ---------------------------------------------------------------------------
const ConversationBridge = ({ pendingTraceIdRef, children }) => {
  const { registerThreadRuntime, reportActivity, reportTurnFinished, reportRunning } = useCopilotMin();
  const threadRuntime = useThreadRuntime();
  const messages = useThread((s) => s.messages);
  const isRunning = useThread((s) => s.isRunning);
  // The tool running for the answer being written: the trigger shows it while the panel is closed.
  const runningLabel = useThread((s) => {
    const last = s.messages[s.messages.length - 1];
    return last?.role === 'assistant' ? activityLabel(last.metadata?.custom) : null;
  });
  const prevIsRunningRef = useRef(false);
  const runStartedAtRef = useRef(null); // for the Stop event's elapsed_ms
  const [traceIdMap, setTraceIdMap] = useState(() => new Map());
  // A report the agent just produced, waiting to be shown in the preview
  // panel. Lives here (not in the panel UI) so a report that lands while the
  // panel is closed still opens as soon as the user comes back to the chat.
  const [autoReport, setAutoReport] = useState(null);
  const clearAutoReport = useCallback(() => setAutoReport(null), []);

  useEffect(() => {
    registerThreadRuntime(threadRuntime);
    return () => registerThreadRuntime(null);
  }, [registerThreadRuntime, threadRuntime]);

  useEffect(() => {
    reportRunning({ active: isRunning, label: isRunning ? runningLabel : null });
  }, [reportRunning, isRunning, runningLabel]);
  useEffect(() => () => reportRunning({ active: false, label: null }), [reportRunning]);

  useEffect(() => {
    if (!prevIsRunningRef.current && isRunning) runStartedAtRef.current = Date.now();
    const finished = prevIsRunningRef.current && !isRunning;
    const lastMsg = messages[messages.length - 1];
    let cancelled = false;
    if (finished) {
      if (lastMsg?.role === 'assistant' && pendingTraceIdRef?.current) {
        const key = lastMsg.id ?? messages.length - 1;
        const traceId = pendingTraceIdRef.current;
        setTraceIdMap((prev) => new Map(prev).set(key, traceId));
      }
      pendingTraceIdRef.current = null;
      cancelled = lastMsg?.status?.type === 'incomplete' && lastMsg?.status?.reason === 'cancelled';
      reportTurnFinished({ cancelled });
      if (!cancelled && lastMsg?.role === 'assistant') {
        // A revised report replaces the previous preview; the last PDF link
        // in the answer is the newest version.
        const ref = findLastPdfRef(lastMsg.content.find((p) => p.type === 'text')?.text);
        if (ref) setAutoReport(ref);
      }
    }
    prevIsRunningRef.current = isRunning;

    // Streaming deltas only change the last message's text, so the snapshot is
    // taken on message count and running-state changes rather than per chunk;
    // the final answer text is captured when the run ends.
    reportActivity({
      messages: messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map(storedMessageOf),
      isRunning,
      cancelled,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, isRunning]);

  return children({ traceIdMap, autoReport, clearAutoReport, runStartedAtRef });
};

// ThreadedCopilot owns the runtime; it is remounted (via the provider's
// threadKey) whenever a conversation starts, is discarded, or is restored.
const ThreadedCopilot = ({ adapter, initialMessages, pendingTraceIdRef, children }) => {
  const runtime = useLocalRuntime(adapter, { initialMessages: initialMessages?.length ? initialMessages : undefined });
  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ConversationBridge pendingTraceIdRef={pendingTraceIdRef}>
        {children}
      </ConversationBridge>
    </AssistantRuntimeProvider>
  );
};


// ---------------------------------------------------------------------------
// Streaming adapter
// ---------------------------------------------------------------------------
// `conversationRef` points at the conversation the runtime belongs to, so the
// request always carries that conversation's session. The case (and the rest
// of the screen) is read from `screenRef` at send time: a chat follows the
// user across pages.
// Wraps an adapter so one place measures the whole turn: how long the answer
// took and whether it finished or failed. A user stop aborts the signal and
// produces no event here — handleStop already tracked it. reportModeRef is
// read before the inner run resets it so the event carries the right mode.
function withAnalytics(adapter, reportModeRef, trackRef) {
  return {
    async *run(opts) {
      const startedAt = Date.now();
      const mode = reportModeRef?.current === true ? 'report' : 'qa';
      let finalText = '';
      try {
        for await (const chunk of adapter.run(opts)) {
          finalText = chunk.content[0]?.text ?? finalText;
          yield chunk;
        }
        trackRef?.current?.('Receive copilot response', {
          mode,
          duration_ms: Date.now() - startedAt,
          ...describeResponse(finalText),
        });
      } catch (err) {
        if (!opts.abortSignal?.aborted) {
          // Only a bounded classification goes out: a stream error's message
          // is free-form backend text that may echo the prompt.
          const httpStatus = Number(/^Copilot API error: (\d+)$/.exec(err?.message ?? '')?.[1]) || null;
          trackRef?.current?.('Copilot response error', {
            mode,
            duration_ms: Date.now() - startedAt,
            error_type: httpStatus ? 'http' : err instanceof TypeError ? 'network' : 'stream',
            http_status: httpStatus,
          });
        }
        throw err;
      }
    },
  };
}

function buildAdapter(conversationRef, screenRef, isSuperAdmin = false, onTraceIdRef = null, reportModeRef = null, interruptRef = null) {
  return {
    async *run({ messages, abortSignal }) {
      const { sessionId } = conversationRef.current ?? {};
      // The screen the user is on right now, not the one the chat was opened on:
      // a chat follows the user across pages and cases.
      const screen = screenRef.current;
      const headers = buildCopilotHeaders(sessionId);

      // A turn triggered by the Report-templates dropdown goes out with mode
      // 'report'. Everything else is a regular 'qa' message: the agent builds a
      // file on its own when a typed message asks for one.
      const isReportTurn = reportModeRef?.current === true;
      if (reportModeRef) reportModeRef.current = false;

      // Report turns send no messages: the API turns the mode into the question
      // itself. Including the visible bubble text would be redundant, and
      // prior history would wrongly scope the report to the previous question.
      // An approval answer sends no messages: Copilot continues the paused run.
      // Kept until the request is accepted, so a retry after a network error still carries the decision.
      const interruptResponse = interruptRef?.current ?? null;

      const apiMessages = isReportTurn || interruptResponse
        ? []
        : messages
            .filter((m) => m.role === 'user' || m.role === 'assistant')
            .map((m) => ({
              role: m.role,
              text: m.content.find((c) => c.type === 'text')?.text ?? '',
            }));

      const response = await fetch('/API/v2/copilot/chat/stream', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          // No case on screen: the key is left out and Copilot answers without one.
          ...(screen.narrativeId ? { narrative_id: screen.narrativeId } : {}),
          mode: isReportTurn ? 'report' : 'qa',
          messages: apiMessages,
          osavul_context: osavulContextOf(screen),
          ...(interruptResponse ? { interrupt_response: interruptResponse } : {}),
        }),
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`Copilot API error: ${response.status}`);
      }
      if (interruptRef) interruptRef.current = null;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      let buffer = '';
      // The tool calls of this answer, shown under it as its Steps.
      let steps = [];
      // The tool the model has just started to call, until that tool's own step takes over.
      let status = null;
      // A change Copilot paused on, waiting for the user's yes or no.
      let approval = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        let streamDone = false;
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (raw === '[DONE]') { streamDone = true; break; }
          let parsed;
          try {
            parsed = JSON.parse(raw);
          } catch (err) {
            if (raw) {
              const payload = isSuperAdmin ? raw : '[SSE_PAYLOAD_REDACTED]';
              console.error('SSE parse error, skipping chunk:', payload, err);
            }
            continue;
          }
          if (parsed.error) {
            throw new Error(parsed.error);
          }
          if (parsed.trace_id) {
            onTraceIdRef?.current?.(parsed.trace_id);
            continue;
          }
          if (parsed.event) {
            // The run failed on the server: end the answer with the inline error, not an empty message.
            if (parsed.event.type === 'error') throw new Error('Copilot run failed');
            // Text streamed before a tool call is the model narrating ("I'll check..."), not the answer: the moment the
            // call is announced (status) or starts (tool), it moves into the Steps, so the answer starts after the last tool.
            if (parsed.event.type === 'status' || (parsed.event.type === 'tool' && parsed.event.phase === 'start')) {
              steps = addNarration(steps, accumulated);
              accumulated = '';
            }
            if (parsed.event.type === 'status') {
              status = statusLabel(parsed.event);
              continue;
            }
            if (parsed.event.type === 'tool') status = null;
            steps = applyStepEvent(steps, parsed.event);
            if (parsed.event.type === 'usage') {
              approval = approvalOf(parsed.event);
              // the paused tool never ends in this run
              if (approval) steps = finishRunning(steps);
            }
            continue;
          }
          accumulated +=
            parsed.choices?.[0]?.delta?.content ??
            parsed.content ??
            parsed.text ??
            parsed.delta ??
            '';
        }

        yield {
          content: [{ type: 'text', text: accumulated }],
          ...(steps.length || approval || status
            ? {
                metadata: {
                  custom: {
                    ...(steps.length ? { steps } : {}),
                    ...(approval ? { approval } : {}),
                    ...(status ? { status } : {}),
                  },
                },
              }
            : {}),
        };
        if (streamDone) return;
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------
// Mounted once, app-wide (inside CopilotMinProvider). Case pages register
// themselves with useCopilotMinPage; everything else comes from the provider.
export const CopilotMinButton = memo(() => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const copilot = useCopilotMin();
  const {
    visible,
    conversation,
    isOpen,
    unread,
    threadKey,
    canStartNewChat,
    open,
    close,
    startNewChat,
    cancelRun,
    lastTurnModeRef,
    getThreadSnapshot,
    running,
    screen,
  } = copilot;
  // 'case' | 'dashboard' | 'search': what the suggestions and the placeholder talk about.
  const context = screen.surface;

  const isSuperAdmin = currentUser?.is_super_admin === true;
  const pendingTraceIdRef = useRef(null);
  // Set by the Report-templates dropdown for exactly one turn: that request
  // goes out with mode 'report' (backend maps it to the report instruction).
  const reportModeRef = useRef(false);
  // Set by the approval card for one turn: that request carries the user's yes or no.
  const interruptRef = useRef(null);
  const onTraceIdRef = useRef((traceId) => {
    pendingTraceIdRef.current = traceId;
  });
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  const screenRef = useRef(screen);
  screenRef.current = screen;

  // Amplitude tracker pre-bound to the current conversation. Case and session
  // are read at call time so events after "New chat" carry the new session.
  const track = useCallback(
    (event, props) =>
      trackCopilotEvent(
        event,
        {
          narrative_id: screenRef.current?.narrativeId ?? null,
          context,
          session_id: conversationRef.current?.sessionId ?? null,
          ...props,
        },
        currentUser,
      ),
    [context, currentUser],
  );
  const trackRef = useRef(track);
  trackRef.current = track;

  const adapter = useMemo(
    () => withAnalytics(buildAdapter(conversationRef, screenRef, isSuperAdmin, onTraceIdRef, reportModeRef, interruptRef), reportModeRef, trackRef),
    [isSuperAdmin],
  );

  // The trigger is unmounted while the dialog is open, so once it comes back
  // after a close, put keyboard focus back on it (dialog focus return).
  const triggerRef = useRef(null);
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen && wasOpenRef.current) triggerRef.current?.focus();
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const handleOpen = useCallback(() => {
    const { messagesCount, isRunning } = getThreadSnapshot();
    track('Open copilot', {
      has_history: messagesCount > 0,
      history_length: messagesCount,
      answer_in_progress: isRunning,
      has_unread_answer: unread,
    });
    open();
  }, [track, open, unread, getThreadSnapshot]);

  if (!visible) return null;

  const working = running.active;
  const workingLabel = running.label || t('Thinking');
  let triggerLabel = t('Open Osavul Copilot');
  if (working) triggerLabel = `${t('Open Osavul Copilot, answer in progress')}: ${workingLabel}`;
  else if (unread) triggerLabel = t('Open Osavul Copilot, new answer');

  return (
    <CopilotAnalyticsContext.Provider value={track}>
      {!isOpen && (
        <button
          ref={triggerRef}
          type="button"
          className={styles.triggerButton}
          onClick={handleOpen}
          aria-label={triggerLabel}
          aria-haspopup="dialog"
        >
          <span className={working ? styles.workingSpinner : styles.aiIcon} aria-hidden="true">
            {!working && <AiIcon />}
          </span>
          <span className={styles.triggerText}>{working ? workingLabel : t('Osavul Copilot')}</span>
          {unread && !working && <span className={styles.unreadDot} aria-hidden="true" />}
        </button>
      )}

      {/* The runtime lives for the whole conversation; only the panel is
          shown and hidden, so an answer keeps streaming while it is closed. */}
      {conversation && (
        <ThreadedCopilot
          key={threadKey}
          adapter={adapter}
          initialMessages={conversation.initialMessages}
          pendingTraceIdRef={pendingTraceIdRef}
        >
          {({ traceIdMap, autoReport, clearAutoReport, runStartedAtRef }) =>
            isOpen ? (
              <div
                className={styles.modalOverlay}
                role="dialog"
                aria-modal="true"
                aria-label={t('Osavul Copilot')}
              >
                <CopilotInner
                  t={t}
                  onClose={close}
                  onNewChat={startNewChat}
                  onStop={cancelRun}
                  canStartNewChat={canStartNewChat}
                  context={context}
                  conversation={conversation}
                  traceIdMap={traceIdMap}
                  autoReport={autoReport}
                  clearAutoReport={clearAutoReport}
                  reportModeRef={reportModeRef}
                  interruptRef={interruptRef}
                  lastTurnModeRef={lastTurnModeRef}
                  runStartedAtRef={runStartedAtRef}
                />
              </div>
            ) : null
          }
        </ThreadedCopilot>
      )}
    </CopilotAnalyticsContext.Provider>
  );
});
